import { useEffect, useState } from "react";
import api from "../../lib/api";


/*
 * Iter-50 — Mission Watch marketing counter, threshold-gated.
 *
 * Below _ADOPTION_THRESHOLD active nonprofit customers, we deliberately
 * DO NOT show numbers. A live counter that reads "83 donation forms
 * watched · 1 nonprofit" tells first-time visitors "this product just
 * launched" — the opposite of social proof. Instead we render a
 * timeless value statement that emphasizes what Mission Watch does,
 * not how many customers it has yet.
 *
 * Once we cross the threshold the numeric mode auto-activates and the
 * copy shifts to platform totals ("N donation forms protected · M
 * nonprofits protected · P pages monitored"). Everything comes from
 * /api/public/stats/donation-coverage — no configuration, no manual
 * flag flip.
 */
const _ADOPTION_THRESHOLD = 20;
const _VALUE_STATEMENTS = [
  "Every donation page checked every hour.",
  "Protection running 24/7.",
  "Quietly watching the pages your donors depend on.",
];
// Rotate the statement per-visit so a page reload doesn't feel static,
// but pin one per render so it doesn't flicker across polls.
const _pickStatement = () =>
  _VALUE_STATEMENTS[Math.floor(Math.random() * _VALUE_STATEMENTS.length)];


export default function LiveDonationCounter({ className = "" }) {
  const [stats, setStats] = useState(null);
  const [statement] = useState(_pickStatement);

  useEffect(() => {
    let mounted = true;
    let timer;
    const load = () => {
      api.get("/public/stats/donation-coverage")
        .then(({ data }) => { if (mounted) setStats(data); })
        .catch(() => {});
    };
    load();
    timer = setInterval(load, 45000);
    return () => { mounted = false; clearInterval(timer); };
  }, []);

  if (!stats) return null;

  const activeOrgs = stats.active_orgs || 0;
  const belowThreshold = activeOrgs < _ADOPTION_THRESHOLD;

  // ---- Below threshold: timeless value statement, no numbers -------------
  if (belowThreshold) {
    return (
      <div
        className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/[0.04] border border-emerald-400/20 text-white/80 ${className}`}
        data-testid="live-donation-counter-statement"
        aria-live="polite"
      >
        <span className="relative flex w-2 h-2">
          <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
          <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
        </span>
        <span className="text-[13px] leading-none">{statement}</span>
      </div>
    );
  }

  // ---- At/above threshold: platform totals -------------------------------
  // Each field falls back to the corresponding 7d window number if the
  // total field somehow arrives as zero (e.g., first request in the
  // window after a fresh deploy). Never renders a bare "0".
  const forms = stats.donation_forms_protected || stats.donation_links_watched_last_7d || 0;
  const orgs  = activeOrgs;
  const pages = stats.pages_monitored || 0;

  if (!forms && !pages) {
    // Extremely unlikely once threshold is met, but fall back to the
    // safe statement rather than "0 donation forms protected".
    return (
      <div
        className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/[0.04] border border-emerald-400/20 text-white/80 ${className}`}
        data-testid="live-donation-counter-statement"
        aria-live="polite"
      >
        <span className="relative flex w-2 h-2">
          <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
          <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
        </span>
        <span className="text-[13px] leading-none">{statement}</span>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 rounded-full bg-white/[0.04] border border-emerald-400/20 text-white/80 ${className}`}
      data-testid="live-donation-counter"
      aria-live="polite"
    >
      <span className="relative flex w-2 h-2">
        <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
        <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
      </span>
      {forms > 0 && (
        <span className="text-[13px] leading-none">
          <span className="font-semibold text-emerald-300 tabular-nums" data-testid="counter-forms">
            {forms.toLocaleString()}
          </span>
          <span className="ml-1.5 text-white/75">
            donation form{forms === 1 ? "" : "s"} protected
          </span>
        </span>
      )}
      {orgs > 0 && (
        <span className="text-[13px] leading-none text-white/60">
          <span className="tabular-nums text-white/85" data-testid="counter-orgs">
            {orgs.toLocaleString()}
          </span>
          <span className="ml-1.5">nonprofit{orgs === 1 ? "" : "s"} protected</span>
        </span>
      )}
      {pages > 0 && (
        <span className="text-[13px] leading-none text-white/60">
          <span className="tabular-nums text-white/85" data-testid="counter-pages">
            {pages.toLocaleString()}
          </span>
          <span className="ml-1.5">page{pages === 1 ? "" : "s"} monitored</span>
        </span>
      )}
    </div>
  );
}
